import { useCallback, useEffect, useRef, useState } from "react";

import type { PaymentIntent } from "@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, InfoBar, LogSentryException, Modal, Spinner } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type { UseFormRegisterReturn } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { ReactComponent as Icon_ApplePay24 } from "../../assets/images/ApplePay24.svg";
import { ReactComponent as Icon_CreditCardFilled24 } from "../../assets/images/CreditCardFilled24.svg";
import {
  useBilling,
  useUpdateCardFinalizeMutation,
  useUpdateCardStartMutation,
} from "../../hooks/useBilling";
import usePrevious from "../../hooks/usePrevious";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  APPLE_PAY_NOT_SUPPORTED,
  ApplePayButtonId,
  ChargebeePaymentType,
  PaymentTypes,
  useChargebee,
} from "../../lib/ChargebeeAdapter";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalShownAction,
} from "../../lib/TrackingAdapter";
import { mapChargebeeErrorMessages } from "../ChargebeeCheckout/ChargebeeCheckoutSchema";
import CreditCardInput from "../CreditCardInput/CreditCardInput";
import type { CreditCardInputValue } from "../CreditCardInput/CreditCardInputTypes";
import { Tiles } from "../Tiles/Tiles";

import style from "./PaymentUpdateModal.module.css";

type PaymentUpdateModalProps = {
  teamID: string | undefined;
  onClose: () => void;
  onSuccess?: () => void;
  open: boolean;
  initialError?: React.ReactNode;
  tertiaryText?: string;
  ctaText?: string;
  onlyCardUpdate?: boolean;
};

const schema = z
  .object({
    paymentType: z
      .enum(PaymentTypes)
      .nullable()
      .optional()
      .superRefine((val, ctx) => {
        if (!val) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please select a payment method",
            path: [],
          });
        }
      }),
    card: z.custom<CreditCardInputValue>().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentType !== ChargebeePaymentType.Card) {
      return;
    }

    if (!data.card) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        type: "string",
        minimum: 1,
        inclusive: true,
        exact: false,
        message: "Please provide your card information",
        path: ["card"],
      });
    }

    if (data.card?.error) {
      ctx.addIssue({ code: "custom", message: data.card.error.message, path: ["card"] });
    }
  });

export type PaymentUpdateFormData = z.infer<typeof schema>;

export default function PaymentUpdateModal({
  teamID,
  onClose,
  onSuccess,
  open,
  initialError,
  tertiaryText,
  ctaText = "Save",
}: PaymentUpdateModalProps) {
  const { t, m } = useTranslation();
  const { isLoading: isChargebeeLoading, isApplePaySupported } = useChargebee();

  const methods = useForm<PaymentUpdateFormData>({
    resolver: zodResolver(schema),
    values: {
      paymentType:
        !isChargebeeLoading && !isApplePaySupported ? ChargebeePaymentType.Card : undefined,
    },
  });

  const tracker = useTracker();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isSubmitted },
    reset,
    setError,
    clearErrors,
    watch,
  } = methods;

  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);

  const [submitPaymentErrorMessage, setSubmitPaymentErrorMessage] = useState<
    { title: string; description: string } | undefined
  >(undefined);

  const paymentType = watch("paymentType");
  const {
    mutateAsync: mutateUpdateCardStart,
    error: updateCardStartError,
    reset: resetUpdateCardStart,
    isPending: isUpdateCardStartPending,
  } = useUpdateCardStartMutation({ teamID, paymentType });

  const {
    mutateAsync: mutateUpdateCardFinalize,
    isError: isUpdateCardFinalizeError,
    reset: resetUpdateCardFinalize,
  } = useUpdateCardFinalizeMutation(teamID, {
    onSuccess: () => {
      toast.success(t(m.Checkout.PaymentError.PaymentInfoUpdated));
      onSuccess?.();
      onClose();

      if (paymentType) {
        tracker.trackUpdatePaymentSourceSuccess({
          paymentType,
        });
      } else {
        LogSentryException({
          messagePrefix: "Payment update modal success",
          error: new Error("Payment type is undefined on success"),
        });
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        if (error.name === APPLE_PAY_NOT_SUPPORTED || error.message === APPLE_PAY_NOT_SUPPORTED) {
          tracker.trackApplePaySetupFailed({
            location: "payment_update_modal",
          });
          setSubmitPaymentErrorMessage({
            title: t(m.Checkout.PaymentError.ApplePaySetupFailedTitle),
            description: t(m.Checkout.PaymentError.ApplePaySetupFailed),
          });
          return;
        }

        const errorMessages = mapChargebeeErrorMessages(error.message);
        // If mapCheckoutErrorMessages returns undefined error will be displayed in the input
        if (!errorMessages) {
          setError("card", { message: error.message });
        } else {
          setSubmitPaymentErrorMessage({
            title: t(errorMessages.titleMessage),
            description: t(errorMessages.descriptionMessage),
          });
        }
      }
    },
  });

  const { data: billingData } = useBilling(teamID);

  const resetPaymentIntent = useCallback(async () => {
    resetUpdateCardStart();
    resetUpdateCardFinalize();
    if (billingData?.primaryPaymentSource) {
      try {
        const { paymentIntent } = await mutateUpdateCardStart();
        setPaymentIntent(paymentIntent);
      } catch {
        // Handled by react-query
      }
    }
  }, [
    resetUpdateCardStart,
    resetUpdateCardFinalize,
    mutateUpdateCardStart,
    billingData?.primaryPaymentSource,
  ]);

  const previousPaymentType = usePrevious(paymentType);
  useEffect(() => {
    if (paymentType && paymentType !== previousPaymentType && previousPaymentType !== undefined) {
      (async () => {
        clearErrors("card");
        resetPaymentIntent();
      })();
    }
  }, [clearErrors, paymentType, previousPaymentType, resetPaymentIntent]);

  useEffect(() => {
    if (open) {
      (async () => {
        reset();
        resetPaymentIntent();
      })();
    }
  }, [open, reset, resetPaymentIntent]);

  const isApplePayModalOpen = paymentType === ChargebeePaymentType.ApplePay && isSubmitting;
  const hasTrackedApplePayModalModalOpen = useRef(false);
  useEffect(() => {
    if (isApplePayModalOpen && !hasTrackedApplePayModalModalOpen.current) {
      hasTrackedApplePayModalModalOpen.current = true;
      tracker.trackModalShown(ModalShownAction.APPLE_PAY_CONFIRMATION);
    }

    return () => {
      hasTrackedApplePayModalModalOpen.current = false;
    };
  }, [isApplePayModalOpen, tracker]);

  const creditCardInputRegisterProps: Omit<UseFormRegisterReturn<"card">, "ref"> &
    Partial<Pick<UseFormRegisterReturn<"card">, "ref">> = register("card");
  delete creditCardInputRegisterProps.ref; // This is a hack to not throw console errors

  return (
    <Modal open={open} title="Payment method" size="medium" onClose={onClose}>
      <FormProvider {...methods}>
        <form
          className={style.paymentUpdateForm}
          onSubmit={handleSubmit(async (data: PaymentUpdateFormData) => {
            try {
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.EDIT_PAYMENT_INFORMATION_MODAL,
                details: paymentType ? paymentType : undefined,
              });
              await mutateUpdateCardFinalize({
                data,
                paymentIntent,
                teamID,
              });
            } catch (error) {
              // This is to not throw Unhandled Promise Rejection error
            }
          })}
        >
          {isApplePaySupported && (
            <>
              <Tiles
                registerAttrs={register("paymentType")}
                options={[
                  {
                    icon: <Icon_ApplePay24 />,
                    value: ChargebeePaymentType.ApplePay,
                    label: "Apple Pay",
                  },
                  {
                    icon: <Icon_CreditCardFilled24 />,
                    value: ChargebeePaymentType.Card,
                    label: "Credit/debit card",
                  },
                ]}
              />
              {errors.paymentType && (
                <span className={style.errorMessage}>{errors.paymentType.message}</span>
              )}
            </>
          )}

          {!isSubmitted && !isSubmitting && initialError}

          <div
            className={classNames(
              style.subgroup,
              paymentType !== ChargebeePaymentType.Card && style.hidden
            )}
          >
            <span className={style.title}>Credit/debit card</span>
            <CreditCardInput {...creditCardInputRegisterProps} control={control} />
            {tertiaryText && <div className={style.tertiary}>{tertiaryText}</div>}
          </div>

          {isUpdateCardFinalizeError && submitPaymentErrorMessage && (
            <InfoBar
              title={submitPaymentErrorMessage.title}
              text={submitPaymentErrorMessage.description}
              variant="critical"
            />
          )}

          <div className={style.actionButtons}>
            <Button
              type="submit"
              loading={isUpdateCardStartPending}
              disabled={!paymentIntent || isSubmitting || Boolean(Object.keys(errors).length)}
            >
              {ctaText}
            </Button>
          </div>

          {(isSubmitting || isChargebeeLoading) && (
            <div className={style.overlay}>
              <Spinner />
              {isSubmitting && paymentType === ChargebeePaymentType.Card && (
                <span>Contacting your card issuer...</span>
              )}
            </div>
          )}

          {updateCardStartError && (
            <div className={style.overlay}>
              <InfoBar variant="critical" title="Something went wrong. Please try again." />
            </div>
          )}
        </form>
        <Modal
          title="Apple Pay"
          open={isApplePayModalOpen}
          size="small"
          onClose={() => {
            reset(undefined, { keepDirtyValues: true });
            resetUpdateCardFinalize();
          }}
        >
          <div className={style.applePayButtonContainer}>
            <Button className={style.applePayButtonPlaceholder} loading />
            <div
              id={ApplePayButtonId}
              onClick={() => {
                tracker.trackApplePayButtonClicked({ location: "payment_update_modal" });
              }}
            />
          </div>
        </Modal>
      </FormProvider>
    </Modal>
  );
}
